// Defaults centralizados de la configuración inicial de cualquier personaje.
// Este archivo es la ÚNICA fuente de verdad para:
//   1. Keybindings por defecto del control panel.
//   2. Macros por defecto (slots 1..6 con teclas 1..6, sin item/spell asignado).
//
// Para extender:
//   - Agregar una nueva acción al keybinding default → push a DEFAULT_KEY_BINDINGS
//     usando el id que definas en engine/config.js → nameKeyCode.
//   - Cualquier cambio a estos defaults → BUMPEAR `KEY_CONFIG_VERSION`. La
//     próxima vez que un usuario cargue /play, su localStorage se compara con
//     la versión actual y si difiere se aplican los defaults nuevos (también
//     el botón "Restaurar predeterminado" usa estos valores).
//
// keyCode reference (códigos JS keydown estándar):
//   17=Ctrl  37=←  38=↑  39=→  40=↓
//   48-57 = '0'..'9'
//   65-90 = 'A'..'Z' (case insensitive)

// Bump esto cada vez que cambie DEFAULT_KEY_BINDINGS o DEFAULT_MACROS.
// Si el localStorage del user trae una versión distinta, hacemos override
// con los defaults nuevos antes de hidratar el state. Esto es lo que
// "actualiza" a todos los personajes existentes a la nueva config base.
//
// v8-2026-05-28 → sumamos spellKeybindings (ver ticket #207). El bump
// invalida el cache viejo pero no toca el dict de spell bindings (que
// vive en otra storage key per-char `spellKeys_<charName>`).
// v9-2026-05-29 → DEFAULT_MACROS pasa de 6 a 9 slots (teclas 1..9). Bump
// para que los chars existentes hidraten los 3 slots nuevos.
// v10-2026-05-30 → sumamos action `ocultar` (id 13, default O=79) para
// la skill de stealth del ticket #239.
// v11-2026-06-04 → sumamos action `refreshPosition` (id 14, default P=80)
// para resync del sprite del char cuando se bugea visualmente (ticket #287).
export const KEY_CONFIG_VERSION = "v11-2026-06-04";

// Defaults de audio. La lógica es independiente de KEY_CONFIG_VERSION para
// no resetear bindings cuando solo se quiere forzar settings de sonido a
// todos los usuarios. Bump AUDIO_CONFIG_VERSION cuando quieras forzar el
// reset (por ej. usuarios que se quedaron muteados y no saben cómo activarlo).
export const AUDIO_CONFIG_VERSION = "v1-2026-05-22";
export const DEFAULT_AUDIO = {
    musicEnabled: true,
    // 35% intencional — filosofía inmersiva "casi ausente, casi presente".
    // El user sube si quiere, pero el default no debe competir con SFX
    // ni distraer del juego. Match con la sensibilidad de games AAA
    // cinematográficos (Dark Souls, Witcher 3 default ~25-35%).
    musicVolume: 35,
    ambienceVolume: 50,
    effectsVolume: 70
};

// Bindings de acciones del juego — ids 0..12 corresponden a las keys del
// objeto config.nameKeyCode (definido en engine/config.js).
// Si agregás una key acá, agregala TAMBIÉN a config.nameKeyCode con el mismo
// id, y al label list de la modal de configuración (pages/play/index.js).
export const DEFAULT_KEY_BINDINGS = {
    0: 87, // flechaArriba  → W
    1: 83, // flechaAbajo   → S
    2: 65, // flechaIzq     → A
    3: 68, // flechaDerecha → D
    4: 85, // usar          → U
    5: 17, // atacar        → Ctrl
    6: 81, // agarrar       → Q
    7: 84, // tirar         → T
    8: 69, // equipar       → E
    9: 72, // domar         → H
    10: 74, // robar        → J
    11: 76, // seguro       → L
    12: 67, // meditar      → C
    13: 79, // ocultar      → O
    14: 80 // refreshPosition → P (resync sprite con server)
};

// Macros regulares: 9 slots con teclas 1..9 pre-asignadas pero SIN item/spell.
// El usuario los asigna en runtime (right-click → modal de macro config).
//
// idItem (canónico) + idPosItem (slot actual): el bug viejo era que el
// macro guardaba SOLO el slot, y al swappear items en el inv el macro
// disparaba sobre lo que quedara en ese slot (ej. usar poción roja se
// volvía equipar armadura). Ahora se guarda idItem canónico y el
// triggerMacro resuelve el slot vía findIdPosByIdItem. idPosItem queda
// como cache/sugerencia inicial.
export const DEFAULT_MACROS = [
    { key: "49", keyChar: "1", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 1
    { key: "50", keyChar: "2", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 2
    { key: "51", keyChar: "3", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 3
    { key: "52", keyChar: "4", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 4
    { key: "53", keyChar: "5", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 5
    { key: "54", keyChar: "6", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 6
    { key: "55", keyChar: "7", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 7
    { key: "56", keyChar: "8", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" }, // 8
    { key: "57", keyChar: "9", idPosItem: "", idItem: "", idSpell: "", idPosSpell: "", img: "" } // 9
];

// Helper: cuántos slots de macros tiene el char. Usado para padding
// retroactivo cuando el server/localStorage devuelve un array más corto
// (chars existentes con 6 macros viejas).
export const MACRO_COUNT = DEFAULT_MACROS.length;

// Helpers — clone deep para no leakear referencias mutables al state.
export function cloneDefaultBindings() {
    return Object.assign({}, DEFAULT_KEY_BINDINGS);
}

export function cloneDefaultMacros() {
    return DEFAULT_MACROS.map(m => Object.assign({}, m));
}
